import { BehaviorSubject, catchError, concatMap, forkJoin, map, merge, Observable, of, Subscription, switchMap, tap, timer } from 'rxjs';
import { AuthService } from './../../../service/auth.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractDto, AgentDto, TicketBundleDto, TicketDto, ResultListDto, WorkflowDto, WorkflowStepDto, WorkflowStepStatusDto, TicketModeEnum } from '../../../service/dto';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { HttpTicketService } from '../../../service/http-ticket.service';
import { NzTableQueryParams } from 'ng-zorro-antd/table';

export class WorkflowWrapper {
	payload!: WorkflowDto;
	_visible!: boolean | undefined;

	get hashCode(): number {
		return (this.payload.hashCode * 10) + (this.visible ? 1 : 0);
	}

	get visible(): boolean {
		if (this._visible === undefined) {
			this._visible = (localStorage.getItem(`workflow_visible_${this.payload.id}`) ?? 'true') === 'true';
		}
		return this._visible;
	}

	set visible(input: boolean) {
		this._visible = input;
		localStorage.setItem(`workflow_visible_${this.payload.id}`, input ? 'true' : 'false');
	}
}

@Component({
	selector: 'app-ticket-dashboard',
	standalone: false,
	templateUrl: './ticket-dashboard.component.html',
	styleUrl: './ticket-dashboard.component.scss'
})
export class TicketDashboardComponent implements OnInit, OnDestroy {
	ticketMode!: TicketModeEnum;
	subscription = new Subscription();
	workflows: WorkflowWrapper[] = [];
	agents: AgentDto[] = [];
	countWithinSla = 0;
	countNearingSla = 0;
	countOutOfSla = 0;
	countAllTickets = 0;

	// Chart data
	slaChartData: any[] = [];
	colorScheme = {
		domain: ['#047e20ff', '#e8ae01ff', '#eb031aff', '#007bff'],
		name: 'slaScheme',
		selectable: true,
		group: {} as any
	};

	tickets: TicketDto[] = [];
	total = 0;
	pageSize = 10;
	pageIndex = 1;
	loading = false;
	currentStepIndex = 0;
	ticketPage: TicketDto[] = [];
	selectedAgent: AgentDto | null = null;
	private reload$ = new BehaviorSubject<void>(undefined);


	constructor(
		public authService: AuthService,
		private ticketService: HttpTicketService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private fb: FormBuilder
	) { }

	ngOnInit(): void {
		this.selectedAgent = this.authService.authState!.agent;

		this.subscription.add(this.reload$.pipe(
			switchMap(() => this.ticketService.getTicketBundle(this.activatedRoute.snapshot.params['ticketMode']?? 'Claim', this.selectedAgent ? this.selectedAgent.id! : null, undefined, 1, 100)))
			.subscribe(result => this.init(result))
		);

		this.subscription.add(this.activatedRoute.params.subscribe(params => {
			this.ticketMode = params['ticketMode']?? 'Claim';
			this.reload$.next();
		}));
		this.subscription.add(timer(30_000).subscribe(() => this.reload$.next()));
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	init(result: TicketBundleDto) {
		this.agents = result.agents;

		this.workflows = result.workflows.map(item => {
			const wrap = new WorkflowWrapper();
			wrap.payload = item;
			return wrap;
		});
		this.visibleWorkflowsChanged();

		this.tickets = result.ticketsToFocusOn.list;
		this.pageIndex = 1;
		this.applyLocalPaging();
	}


	trackByDto(index: number, input: AbstractDto | WorkflowWrapper): number {
		return (input as any)?.hashCode ?? index;
	}

	openWorkflow(workflow: WorkflowDto) {
		if (this.selectedAgent) {
			this.router.navigate(['/ticket', this.ticketMode, workflow.id, 'list'], { queryParams: { agentId: this.selectedAgent.id } });
		} else {
			this.router.navigate(['/ticket', this.ticketMode, workflow.id, 'list']);
		}
	}
	openStep(workflow: WorkflowDto, step: WorkflowStepDto) {
		if (this.selectedAgent) {
			this.router.navigate(['/ticket', this.ticketMode, workflow.id, step.id, 'list'], { queryParams: { agentId: this.selectedAgent.id } });
		} else {
			this.router.navigate(['/ticket', this.ticketMode, workflow.id, step.id, 'list']);
		}

	}
	openStatus(workflow: WorkflowDto, step: WorkflowStepDto, status: WorkflowStepStatusDto) {
		if (this.selectedAgent) {
			this.router.navigate(['/ticket', this.ticketMode, workflow.id, step.id, 'list'], { queryParams: { agentId: this.selectedAgent.id } });
		} else {
			this.router.navigate(['/ticket', this.ticketMode, workflow.id, step.id, 'list']);
		}
	}
	openTicket(input: TicketDto) {
		this.router.navigate(['/ticket', this.ticketMode, input.id]);
	}

	getSlaClass(ticket: TicketDto): 'success' | 'info' | 'warning' | 'error' {
		if (ticket.slaLevel == 'Good') {
			return 'success';
		}
		if (ticket.slaLevel == 'Warning') {
			return 'warning';
		}
		return 'error';
	}
	visibleWorkflowsChanged() {
		let values = this.workflows.filter(item => item.visible == true).map(item => item.payload.steps).flat().map(item => item.statuses).flat().map(item => item.ticketCountWithinSla!);
		if (values.length == 0) {
			this.countWithinSla = 0;
		} else {
			this.countWithinSla = values.reduce((i1, i2) => i1 + i2);
		}
		values = this.workflows.filter(item => item.visible == true).map(item => item.payload.steps).flat().map(item => item.statuses).flat().map(item => item.ticketCountNearingSlaLimit!);
		if (values.length == 0) {
			this.countNearingSla = 0;
		} else {
			this.countNearingSla = values.reduce((i1, i2) => i1 + i2);
		}
		values = this.workflows.filter(item => item.visible == true).map(item => item.payload.steps).flat().map(item => item.statuses).flat().map(item => item.ticketCountOutOfSla!);
		if (values.length == 0) {
			this.countOutOfSla = 0;
		} else {
			this.countOutOfSla = values.reduce((i1, i2) => i1 + i2);
		}
		values = this.workflows.filter(item => item.visible == true).map(item => item.payload.steps).flat().map(item => item.ticketCount!);
		if (values.length == 0) {
			this.countAllTickets = 0;
		} else {
			this.countAllTickets = values.reduce((i1, i2) => i1 + i2);
		}
		this.slaChartData = [
			{ name: 'Within SLA', value: this.countWithinSla },
			{ name: 'Nearing SLA', value: this.countNearingSla },
			{ name: 'Out of SLA', value: this.countOutOfSla }
		];
		this.pageIndex = 1;
		this.applyLocalPaging();
	}

	//Method for chart slice selection
	onSliceSelect(event: any): void {
		console.log('Slice selected:', event);
	}

	getThis(): TicketDashboardComponent {
		return this;
	}
	onQueryParamsChange(that: TicketDashboardComponent, params?: NzTableQueryParams): void {
		const { pageIndex, pageSize } = params ? params : { pageIndex: this.pageIndex, pageSize: this.pageSize };
		that.pageIndex = params?.pageIndex ?? that.pageIndex;
		that.pageSize = params?.pageSize ?? that.pageSize;
		this.applyLocalPaging();
	}


	filterOption = (input: string, option: any): boolean => option.nzLabel.toLowerCase().includes(input.toLowerCase());

	getAuthedAgent(): AgentDto {
		return this.authService.authState!.agent;
	}
	onSelect(value: AgentDto | null) {
		this.selectedAgent = value;
		this.reload$.next();
	}
	applyLocalPaging() {
		const start = (this.pageIndex - 1) * this.pageSize;
		const end = start + this.pageSize;
		let tickets = this.tickets.filter(item => this.isWorkflowVisible(item.workflowId));
		this.total = tickets.length;
		this.ticketPage = tickets.slice(start, end);
	}
	private isWorkflowVisible(id: number): boolean {
		return this.workflows.find(item => item.payload.id == id)?.visible ?? false;
	}
}

